import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import { getCompoundEntityRef } from '@backstage/catalog-model';
import {
  InfoCard,
  LogViewer,
  Progress,
  ResponseErrorPanel,
} from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import { jenkinsApiRef } from '@backstage-community/plugin-jenkins';

// The Jenkins plugin's build page lives at .../builds/:jobFullName/:buildNumber
// (jobFullName URI-encoded). "runs" pages end in /runs, so they don't match.
const BUILD_PAGE_PATH = /\/builds\/([^/]+)\/(\d+)\/?$/;

export const parseBuildPagePath = (
  pathname: string,
): { jobFullName: string; buildNumber: string } | undefined => {
  const match = BUILD_PAGE_PATH.exec(pathname);
  return match
    ? { jobFullName: decodeURIComponent(match[1]), buildNumber: match[2] }
    : undefined;
};

const ConsoleOutputCard = (props: {
  jobFullName: string;
  buildNumber: string;
}) => {
  const { jobFullName, buildNumber } = props;
  const { entity } = useEntity();
  const jenkinsApi = useApi(jenkinsApiRef);

  const [text, setText] = useState<string>();
  const [error, setError] = useState<Error>();
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    setError(undefined);
    jenkinsApi
      .getBuildConsoleText({
        entity: getCompoundEntityRef(entity),
        jobFullName,
        buildNumber,
      })
      .then(result => !cancelled && setText(result.consoleText))
      .catch(e => !cancelled && setError(e))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [jenkinsApi, entity, jobFullName, buildNumber]);

  useEffect(() => load(), [load]);

  let content;
  if (loading) {
    content = <Progress />;
  } else if (error) {
    content = <ResponseErrorPanel error={error} />;
  } else {
    content = (
      <div style={{ height: 600 }}>
        <LogViewer text={text ?? ''} />
      </div>
    );
  }

  return (
    <InfoCard
      title="Console output"
      subheader={`${jobFullName} #${buildNumber}`}
      action={
        <Button size="small" onClick={() => load()} disabled={loading}>
          Reload
        </Button>
      }
    >
      {content}
    </InfoCard>
  );
};

// The Jenkins plugin (0.29.0) can fetch a build's console log but has no screen
// for it; its build page only links to Jenkins. Render this next to
// <EntityJenkinsContent /> in EntityPage.tsx: it shows the log only while the
// plugin's build page is open, readable by everyone who can see the component.
//
// EntityJenkinsContent must stay directly in EntityPage's element tree (not
// inside this component): Backstage discovers routable extensions by walking
// that tree and can't see inside custom components.
export const JenkinsConsoleOutput = () => {
  const { pathname } = useLocation();
  const build = parseBuildPagePath(pathname);

  if (!build) {
    return null;
  }
  return (
    <Grid item>
      <ConsoleOutputCard
        jobFullName={build.jobFullName}
        buildNumber={build.buildNumber}
      />
    </Grid>
  );
};
